// The admin information architecture, derived from the config registry rather
// than declared here. GET /api/settings/metadata carries `domain`, `category` and
// `tier` on every ConfigMetadata, and those three fields ARE the tree:
//
//     Domain → Category → Setting Group → Setting
//
// The fourth level is the disclosure band. The registry has no "group" column, so
// inventing one would mean inventing structure the server does not have; the band
// is real structure the registry does carry (bible §20: normal settings visible,
// advanced under "Advanced options", hardware/infrastructure under
// "Expert / Hardware") and it is what a group means on this screen.
//
// Nothing here filters by tier. Every registry row is in the tree — the band it
// lands in decides whether it is open on arrival, not whether it exists. The old
// page dropped advanced/expert rows from the DOM entirely, which is why a search
// had to raise a "complexity tier" before it could jump to one.

import { EXCLUDED_SETTING_KEYS, orderDomains } from "@/lib/settingsMap";

/**
 * The disclosure bands, outermost first.
 *
 * `expert` is also the infrastructure band. The registry files every
 * infrastructure knob — worker poll intervals, the pg listener reconnect, the
 * login rate limit, the request body cap — at tier "expert", so bible §53's rule
 * that infrastructure never faces an ordinary operator falls out of the registry
 * instead of out of a hand-maintained key list that would go stale.
 */
export const BANDS = [
  { id: "normal", label: "", tier: "simple", openByDefault: true },
  { id: "advanced", label: "Advanced options", tier: "advanced", openByDefault: false },
  { id: "expert", label: "Expert / Hardware", tier: "expert", openByDefault: false },
];

const BAND_OF_TIER = { simple: "normal", advanced: "advanced", expert: "expert" };

/** Which band a registry row belongs to. An unknown tier is treated as simple. */
export const bandOfTier = tier => BAND_OF_TIER[tier] || "normal";

/** True for the keys that must never face an operator at the simple level. */
export const isInfrastructure = meta => bandOfTier(meta?.tier) === "expert";

/** "Bill Content" → "bill-content", so a testid or a node id has no spaces in it. */
export const slug = value =>
  String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const domainNodeId = domain => `domain:${slug(domain)}`;
export const categoryNodeId = (domain, category) => `cat:${slug(domain)}:${slug(category)}`;
export const bandNodeId = (domain, category, band) => `band:${slug(domain)}:${slug(category)}:${band}`;

/**
 * registry list → [{ domain, categories: [{ category, bands: [{ items }] }] }].
 *
 * Excluded keys are dropped here, once, so no consumer of the tree can render one
 * by accident: the tree is the only thing the workspace and the nav both read.
 */
export function buildConfigTree(metadata) {
  const byDomain = new Map();

  for (const meta of Array.isArray(metadata) ? metadata : []) {
    if (!meta || typeof meta.key !== "string" || !meta.key) continue;
    if (meta.key in EXCLUDED_SETTING_KEYS) continue;
    // Registered but retired: nothing reads it (bible §46 — no fake config). Was
    // default_paper_width, shown under Hardware while the printer ignored it.
    if (meta.resolution === "obsolete_alias") continue;

    const domain = meta.domain || "Other";
    const category = meta.category || "General";
    if (!byDomain.has(domain)) byDomain.set(domain, new Map());
    const categories = byDomain.get(domain);
    if (!categories.has(category)) categories.set(category, []);
    categories.get(category).push(meta);
  }

  return orderDomains([...byDomain.keys()]).map(domain => {
    const categories = [...byDomain.get(domain).entries()].map(([category, items]) => {
      const bands = BANDS.map(band => ({
        ...band,
        id: band.id,
        nodeId: bandNodeId(domain, category, band.id),
        items: items.filter(meta => bandOfTier(meta.tier) === band.id),
      })).filter(band => band.items.length > 0);

      return {
        domain,
        category,
        nodeId: categoryNodeId(domain, category),
        bands,
        count: items.length,
      };
    });

    return { domain, nodeId: domainNodeId(domain), categories };
  });
}

/** Where a key lives, or null when the registry does not carry it. */
export function locateSetting(tree, key) {
  for (const domain of tree) {
    for (const category of domain.categories) {
      for (const band of category.bands) {
        if (band.items.some(meta => meta.key === key)) {
          return { domain: domain.domain, category: category.category, band: band.id };
        }
      }
    }
  }
  return null;
}

/** The category a domain opens on — never a blank workspace. */
export function firstCategory(tree, domain) {
  const node = tree.find(entry => entry.domain === domain);
  return node?.categories[0]?.category || "";
}

/** The node the page lands on before the owner has chosen anything. */
export function defaultSelection(tree) {
  const domain = tree[0];
  if (!domain || !domain.categories.length) return null;
  return { kind: "category", domain: domain.domain, category: domain.categories[0].category };
}

/**
 * Every ancestor node id of a category, so navigating to it can open the path
 * rather than dropping the owner on a collapsed tree with nothing revealed.
 */
export function pathToCategory(domain, category, band) {
  const ids = [domainNodeId(domain), categoryNodeId(domain, category)];
  if (band) ids.push(bandNodeId(domain, category, band));
  return ids;
}
